import { Fragment, useEffect, useRef } from 'react';
import { ToolResponse, ChatMessage } from 'src/api/chat';
import { tools } from './tools';

type MessageProps = {
  message: ChatMessage;
  sendResponse: (responses: ToolResponse[]) => Promise<void>;
};

const Iframe = ({ url }: { url: string }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.style.height = `${(iframeRef.current.offsetWidth * 9) / 16}px`;
    }
  }, [iframeRef]);

  return (
    <iframe
      ref={iframeRef}
      style={{ width: '100%', maxWidth: '100%' }}
      className="top-0 left-0 w-full h-full"
      src={url}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="no-referrer"
      allowFullScreen
    ></iframe>
  );
};

export const MessageContent = ({ message }: MessageProps) => {
  const { content } = message;

  if (!content) return null;

  const parseYoutubeTag = (text: string) => {
    const parts = [];
    let currentIndex = 0;

    // Regular expression to match YouTube video tags
    const youtubeTagRegex = /<youtube_video\s+src="([^"]+)">/g;
    let match;

    while ((match = youtubeTagRegex.exec(text)) !== null) {
      // Add text before the tag
      if (match.index > currentIndex) {
        parts.push(text.slice(currentIndex, match.index));
      }

      // Add the YouTube video component
      const videoUrl = match[1];
      parts.push({
        type: 'youtube_video',
        url: videoUrl,
      });

      currentIndex = match.index + match[0].length;

      // Skip the closing tag
      const closingTagIndex = text.indexOf('</youtube_video>', currentIndex);
      if (closingTagIndex !== -1) {
        currentIndex = closingTagIndex + '</youtube_video>'.length;
      }
    }

    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(text.slice(currentIndex));
    }

    return parts;
  };

  const renderYoutubeVideo = (url: string) => {
    try {
      let videoUrl = new URL(url);
      if (!videoUrl.toString().includes('embed')) {
        videoUrl = new URL(
          `https://www.youtube.com/embed/${videoUrl.searchParams.get('v')}`
        );
      }

      videoUrl.searchParams.set('rel', '0');
      videoUrl.searchParams.set('playsinline', '1');
      // this flag is deprecated by YT and does nothing, but we still keepi
      videoUrl.searchParams.set('modestbranding', '1');

      return <Iframe url={videoUrl.toString()} />;
    } catch (error) {
      return null;
    }
  };

  const renderContent = () => {
    if (!content) return null;

    const parts = parseYoutubeTag(content as string);

    return parts.map((part, index) => {
      if (typeof part === 'string') {
        return (
          <Fragment key={index}>
            {part.split('\n').map((line, lineIndex) => (
              <Fragment key={`${index}-${lineIndex}`}>
                {line}
                <br />
              </Fragment>
            ))}
          </Fragment>
        );
      } else if (part.type === 'youtube_video') {
        return <Fragment key={index}>{renderYoutubeVideo(part.url)}</Fragment>;
      }
      return null;
    });
  };

  return (
    <div
      style={{ wordBreak: 'break-word' }}
      className="flex pb-1 flex-col gap-2"
    >
      {renderContent()}
    </div>
  );
};

type MessageToolsProps = {
  toolCalls: ChatMessage['tool_calls'];
  sendResponse: (responses: ToolResponse[]) => Promise<void>;
};

export const MessageTools = ({
  toolCalls,
  sendResponse,
}: MessageToolsProps) => {
  if (!toolCalls?.length) return null;

  return (
    <>
      {toolCalls?.map((toolCall) => {
        const { args, type, name, id } = toolCall;

        const Tool = tools[name as keyof typeof tools];
        return (
          <Tool
            key={id}
            name={name}
            id={id}
            type={type}
            args={args || {}}
            onComplete={(payload) => {
              return sendResponse([
                {
                  type,
                  name,
                  id,
                  ...payload,
                },
              ]);
            }}
          />
        );
      })}
    </>
  );
};

type ToolResultsProps = {
  toolResponses: ChatMessage['tool_responses'];
};

export const MessageToolResults = ({ toolResponses }: ToolResultsProps) => {
  if (!toolResponses?.length) return null;

  return (
    <>
      {toolResponses.map((toolResponse) => {
        const { name, id, type, args } = toolResponse;
        const Tool = tools[name as keyof typeof tools];
        if (!Tool) return null;

        return (
          <Tool
            key={id}
            response={toolResponse}
            name={name}
            id={id}
            type={type}
            args={args || {}}
            onComplete={() => Promise.resolve()}
          />
        );
      })}
    </>
  );
};
